import { TSitecoreContext, TStringField } from '../../../types/sitecore.types';
import { DealerLocatorLandingModel } from '../models/dealerLocatorLanding.model';

export type TPlaceCoordinates = {
  lat: number;
  lng: number;
};

export type TResultViewMode = 'list' | 'map';

export type TDealerResultListItemProps = {
  dealer: TDealershipSuggestion;
  clickedDealerNumber: string | null;
  dealerIdForPinWithHover: string | null;
  markerNumber: number;
  model: DealerLocatorLandingModel;
  country: TCountry | null;
  region: TState | null;
  setDealerIdForPinWithHover: React.Dispatch<React.SetStateAction<string | null>>;
  setClickedDealerNumber: React.Dispatch<React.SetStateAction<string | null>>;
  setDealerIdPinHovered: React.Dispatch<React.SetStateAction<boolean | null>>;
  resultsViewModeInTablet: TResultViewMode;
};

export type TDealerResultsListingProps = {
  dealers: TDealershipSuggestion | TDealershipSuggestion[];
  dealerIdForPinWithHover: string | null;
  model: DealerLocatorLandingModel;
  clickedDealerNumber: string | null;
  isLoadingDealers: boolean;
  country: TCountry | null;
  region: TState | null;
  setDealerIdForPinWithHover: React.Dispatch<React.SetStateAction<string | null>>;
  setClickedDealerNumber: React.Dispatch<React.SetStateAction<string | null>>;
  setDealerIdPinHovered: React.Dispatch<React.SetStateAction<boolean | null>>;
  resultsViewModeInTablet: TResultViewMode;
};

export type TUseSuggestionsParams = {
  pageId: string;
  language: string;
  countryIsoCode: string | string[];
  resultsLimit: number;
};

export type TGooglePlaceSuggestion = {
  description?: string;
  place_id: string;
};

export type TDealership = {
  brand: string;
  cnhOwnershipGroupSAPNumber: string;
  cnhPrimarySAPNumber: string;
  cnhZ3SAPNumber: string;
  country: string;
  countryCode: string;
  dealerName: string;
  dealerNumber: string;
  dealerEmail?: string;
  dealerWebsite?: string;
  dealershipAttributes: {
    website: string;
    awardDesignations?: Array<{
      designationName: string;
      imageUrl: string;
    }>;
    isContractOverride: boolean;
    contractDetails: Array<{
      codeId: string;
      codeName: string;
      longDescription: string;
      shortDescription: string;
    }>;
  };
  hasInternalDetails: string;
  latitude: string;
  longitude: string;
  region: string;
  shippingAddress1: string;
  shippingCity: string;
  shippingFax?: string;
  shippingPhone?: string;
  shippingStateProv: string;
  shippingZip: string;
  superRegion: string;
};
export type TDealershipResult = {
  dealership: TDealership;
  distance?: string;
};

export type TDealershipSuggestion = TDealershipResult & {
  place?: {
    lat: string;
    lng: string;
  };
};

export type TSuggestions = {
  googlePlacesPredictions: TGooglePlaceSuggestion[];
  dealershipResults: TDealershipSuggestion[];
} | null;

export type TFilteredDealerResults = {
  dealerLocatorEquipmentsResults: string;
  dealerLocatorServicesResults: string;
};

export type TDealerLocatorSearchProps = {
  model: DealerLocatorLandingModel;
  onDealerResults: (props: onDealerResultsProps) => void;
  country: TCountry | null;
  setCountry: React.Dispatch<React.SetStateAction<TCountry | null>>;
  region: TState | null;
  setRegion: React.Dispatch<React.SetStateAction<TState | null>>;
  autocompleteService: google.maps.places.AutocompleteService;
  placesService: google.maps.places.PlacesService;
  setUserLocation: React.Dispatch<React.SetStateAction<{ lat: string; lng: string } | null>>;
  setIsLoadingDealers: React.Dispatch<React.SetStateAction<boolean>>;
  filteredDealerResults: TFilteredDealerResults;
  sitecoreContext?: TSitecoreContext;
};

export type TSearchSwithOptionsProps = {
  typeName: SearchType;
  children: any;
};

export type TSearchSwitchProps = {
  type: SearchType;
  children: JSX.Element | JSX.Element[];
};

export type TTextSearchProps = {
  model: DealerLocatorLandingModel;
  countryIsoCode: string;
  label: string;
  onDealerSelect: (dealer: any) => void;
  onGooglePlaceSelect: (googlePlace: any, placeId: string, placeDescription: string, updateUrl: boolean) => void;
  autocompleteService: google.maps.places.AutocompleteService;
  placesService: google.maps.places.PlacesService;
  filteredDealerResults: TFilteredDealerResults;
};

export enum SearchType {
  NoSearch = 'NoSearch',
  SearchByCountryAndRegion = 'SearchByCountryAndRegion',
  SearchByCountryAndLocationOrDealer = 'SearchByCountryAndLocationOrDealer',
  SearchByLocationOrDealer = 'SearchByLocationOrDealer',
  SearchByCountry = 'SearchByCountry',
}

export type TState = {
  name: string;
  gmiCode: string;
};

export type TCountry = {
  Country: string;
  IsoCode: string;
  Regions?: Array<string>;
  states: Array<TState>;
  fipsCode: string;
  latitude: string;
  longitude: string;
  zoom: string;
};

export type TMapSettings = {
  lat: number;
  lng: number;
  zoom: number;
};

export type onDealerResultsProps = {
  results: TDealershipSuggestion | TDealershipSuggestion[];
  place?: TPlaceCoordinates;
  queryText?: string;
  userLocation?: TPlaceCoordinates;
  selectedDealerId?: string;
  placeId?: string;
  updateUrl?: boolean;
};

export type TDealerLocatorMapProps = {
  model: DealerLocatorLandingModel;
  onDealerResults: (props: onDealerResultsProps) => void;
  country: TCountry | null;
  region: TState | null;
  place?: TPlaceCoordinates;
  setAutocompleteService: React.Dispatch<React.SetStateAction<google.maps.places.AutocompleteService | undefined>>;
  setPlacesService: React.Dispatch<React.SetStateAction<google.maps.places.PlacesService | undefined>>;
  dealers: TDealershipSuggestion | TDealershipSuggestion[];
  dealerIdForPinWithHover: string | null;
  setDealerIdForPinWithHover: React.Dispatch<React.SetStateAction<string | null>>;
  setClickedDealerNumber: React.Dispatch<React.SetStateAction<string | null>>;
  setCountry: React.Dispatch<React.SetStateAction<TCountry | null>>;
  setRegion: React.Dispatch<React.SetStateAction<TState | null>>;
  setUserLocation: React.Dispatch<React.SetStateAction<{ lat: string; lng: string } | null>>;
  mapSettings: TMapSettings;
  dealerIdPinHovered: boolean;
  sitecoreContext: TSitecoreContext;
  resultsViewModeInTablet: TResultViewMode;
};

export type TDealerLocatorLandingSettings = {
  initialLatitude: number;
  initialLongitude: number;
  initialZoom: number;
  dealerLocatorCountryState: Array<TCountry>;
  dealerLocatorDefaultCountry: string;
  dealerLocatorGoogleMapsAPIKey: string;
  dealerLocatorSearchType: SearchType;
  showPreselectCountry: boolean;
  maxZoomForSingleResult: number;
  searchRadius: string;
  dealerSearchResult: {
    dealershipResults: TDealershipResult[];
  };
  maxResultsCount?: number;
  enableInteractiveMap: boolean;
  firebaseConfiguration?: TFirebaseConfig;
  enableClusters: boolean;
  enableClickableMap: boolean;
  defaultMobileView?: TResultViewMode;
};

export type TDealerLocatorLandingFilterDetails = Partial<{
  Title: string;
  Code: string;
}>;

export type TDealerLocatorLandingFilters = Partial<{
  Equipments: Array<TDealerLocatorLandingFilterDetails>;
  Services: Array<TDealerLocatorLandingFilterDetails>;
}>;

export type TDealerLocatorLandingFields = Partial<{
  title: TStringField;
  description: TStringField;
  settings: TDealerLocatorLandingSettings;
  pageId: string;
  filters: TDealerLocatorLandingFilters;
  isComponentUnreleased: boolean;
}>;

export type TDealerLocatorLandingProps = Partial<{
  fields: TDealerLocatorLandingFields;
}>;

// -- components props types
export type TSelectedFilterOptions = { [key: string]: Array<number> };

export type TDealerSelectedFiltersState = {
  equipmentSelectedOptions: TSelectedFilterOptions;
  serviceSelectedOptions: TSelectedFilterOptions;
};

export type TDealerSelectedFilterKeyName = 'equipmentSelectedOptions' | 'serviceSelectedOptions';

export type TDealerFilterComponentProps = {
  filters: TDealerLocatorLandingFilters;
  selectedFilters: TDealerSelectedFiltersState;
  updateFilters: (TDealerSelectedFiltersState) => void;
};

export type TCountriesFirebaseCollectionResponse = Array<TCountriesFirebaseCollectionDocResponse>;

export type TCountriesFirebaseCollectionDocResponse = {
  name: string;
  iso_a2: string;
  kml_4326: string;
};

export type TCountriesFirebaseCollection = Array<TCountriesFirebaseCollectionDoc>;

export type TCountriesFirebaseCollectionDoc = Omit<TCountriesFirebaseCollectionDocResponse, 'kml_4326'> & {
  kml_4326: TGeometryData;
};

export type TStatesFirebaseCollectionResponse = Array<TStatesFirebaseCollectionDocResponse>;

export type TStatesFirebaseCollectionDocResponse = {
  geometry: string;
  CNTRY_NAME: string;
  FIPS_CNTRY: string;
  GMI_ADMIN: string;
};

export type TStatesFirebaseCollection = Array<TStatesFirebaseCollectionDoc>;

export type TStatesFirebaseCollectionDoc = Omit<TStatesFirebaseCollectionDocResponse, 'geometry'> & {
  geometry: TGeometryData;
};

export type TGeometry = {
  type: 'Polygon';
  coordinates: [Array<[number, number, number]>];
};

export type TGeometryCollection = {
  type: 'GeometryCollection';
  geometries: Array<TGeometry>;
};

export type TGeometryDoc = { geometry: TGeometry };

export type TGeometryData = TGeometryCollection | TGeometryDoc;

export type TUseContryBordersCoordsProps = {
  model: DealerLocatorLandingModel;
};

export type TUseRegionBordersCoordsProps = {
  model: DealerLocatorLandingModel;
  country: TCountry | null;
};

export type TAreaPolygonData = {
  kml: TGeometryData;
  name: string;
  code: string;
  key?: string;
};

export type TFirebaseConfig = {
  [keys: string]: string;
};

export type TDropDownItem = TCountry | TState | null;

export type TUpdateUrlQueryStringParamsProps = {
  country?: TCountry | null;
  region?: TState | null;
  queryText?: string;
  userLocation?: TPlaceCoordinates;
  selectedDealerId?: string;
  placeId?: string;
  updateUrl?: boolean;
};

export type TDealerLocatorGeoLocationToastmessage = {
  extraClassName?: string;
  showGeoLocationErrorMessage: boolean;
  updateShowGeoLocationErrorMessage: React.Dispatch<React.SetStateAction<boolean>>;
}